<script setup lang="ts">
import { usePersonalAccountDataState } from '../../../../utilities/account/useAccountDataPersonal'
import BaseInputText from '../../../base/BaseInputText.vue'
import {
    AcademicCapIcon,
    BriefcaseIcon,
    CalendarIcon,
    CurrencyEuroIcon,
    SwitchHorizontalIcon,
} from '@heroicons/vue/solid'
import BaseInputSelect from '../../../base/BaseInputSelect.vue'
import { i18n } from '../../../../localization'
import { initPersonalAccountForm } from '../../../../utilities/account/useAccountFormPersonal'
import {
    isRequiredNaturalProfileAttributesForInvestment,
    isRequiredRequiredNaturalProfileAttributes,
} from '../../../../validation'
import BaseInputCheckbox from '../../../base/BaseInputCheckbox.vue'

const { naturalProfile: account } = usePersonalAccountDataState()

const highestEducationalAttainmentOptions = [
    'prefer_not_to_say',
    'no_degree',
    'secondary_school',
    'junior_high_school',
    'high_school_diploma',
    'university',
].map((key) => {
    return {
        key: key,
        label: i18n.global.t('highest_educational_attainment.' + key),
    }
})

const investmentExperienceOptions = ['none', 'up_to_3_years', 'between_3_and_5_years', 'more_than_5_years'].map(
    (key) => {
        return {
            key: key,
            label: i18n.global.t('investment_experience.' + key),
        }
    }
)

const averageInvestmentTransactionOptions = ['none', 'up_to_5', 'between_5_and_10', 'over_10'].map((key) => {
    return {
        key: key,
        label: i18n.global.t('average_investment_transaction.' + key),
    }
})

const averageInvestmentAmountOptions = [
    'up_to_3000_eur',
    'between_3001_and_5000_eur',
    'between_5001_and_10000_eur',
    'over_10000_eur',
].map((key) => {
    return {
        key: key,
        label: i18n.global.t('average_investment_amount.' + key),
    }
})

initPersonalAccountForm('Legal Information')
</script>

<template>
    <div class="grid grid-cols-1 gap-x-4 gap-y-6 px-4 py-5 sm:grid-cols-2 sm:px-6">
        <div class="sm:col-span-1">
            <BaseInputSelect
                v-model="account.highest_educational_attainment"
                :label="$t('account.highest_educational_attainment')"
                name="account.highest_educational_attainment"
                :required="isRequiredRequiredNaturalProfileAttributes('highest_educational_attainment')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('highest_educational_attainment')"
                :icon="AcademicCapIcon"
                :options="highestEducationalAttainmentOptions">
            </BaseInputSelect>
        </div>

        <div class="sm:col-span-1">
            <BaseInputText
                v-model="account.current_occupation"
                :label="$t('account.current_occupation')"
                :required="isRequiredRequiredNaturalProfileAttributes('current_occupation')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('current_occupation')"
                name="account.current_occupation"
                :icon="BriefcaseIcon"></BaseInputText>
        </div>
        <div class="sm:col-span-2">
            <div class="border-t border-gray-300 pb-4" />
            <p class="text-md font-semibold text-gray-700">
                {{ $t('account.experience_investment_forms') }}
            </p>
        </div>

        <div class="mt-2 sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.knowledge_share"
                name="account.knowledge_shares"
                class="mb-4"
                :required="isRequiredRequiredNaturalProfileAttributes('knowledge_share')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('knowledge_share')"
                :label="$t('account.knowledge_share')" />
            <BaseInputSelect
                v-model="account.experience_share"
                :label="$t('account.experience_share')"
                name="account.experience_share"
                :required="isRequiredRequiredNaturalProfileAttributes('experience_share')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('experience_share')"
                :icon="CalendarIcon"
                :options="investmentExperienceOptions">
            </BaseInputSelect>
        </div>
        <div class="mt-2 sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.knowledge_bond"
                name="account.knowledge_bondds"
                class="mb-4"
                :required="isRequiredRequiredNaturalProfileAttributes('knowledge_bond')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('knowledge_bond')"
                :label="$t('account.knowledge_bond')" />
            <BaseInputSelect
                v-model="account.experience_bond"
                :label="$t('account.experience_bond')"
                name="account.experience_bond"
                :required="isRequiredRequiredNaturalProfileAttributes('account.experience_bond')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('account.experience_bond')"
                :icon="CalendarIcon"
                :options="investmentExperienceOptions">
            </BaseInputSelect>
        </div>
        <div class="mt-2 sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.knowledge_participation_paper"
                name="account.knowledge_participation_paper"
                class="mb-4"
                :required="isRequiredRequiredNaturalProfileAttributes('knowledge_participation_paper')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('knowledge_participation_paper')"
                :label="$t('account.knowledge_participation_paper')" />
            <BaseInputSelect
                v-model="account.experience_participation_paper"
                :label="$t('account.experience_participation_paper')"
                name="account.experience_participation_paper"
                :required="isRequiredRequiredNaturalProfileAttributes('experience_participation_paper')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('experience_participation_paper')"
                :icon="CalendarIcon"
                :options="investmentExperienceOptions">
            </BaseInputSelect>
        </div>
        <div class="mt-2 sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.knowledge_crypto_currency"
                name="account.knowledge_crypto_currency"
                class="mb-4"
                :required="isRequiredRequiredNaturalProfileAttributes('knowledge_crypto_currency')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('knowledge_crypto_currency')"
                :label="$t('account.knowledge_crypto_currency')" />
            <BaseInputSelect
                v-model="account.experience_crypto_currency"
                :label="$t('account.experience_crypto_currency')"
                name="account.experience_crypto_currency"
                :required="isRequiredRequiredNaturalProfileAttributes('experience_crypto_currency')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('experience_crypto_currency')"
                :icon="CalendarIcon"
                :options="investmentExperienceOptions">
            </BaseInputSelect>
        </div>
        <div class="mt-2 sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.knowledge_unsecured_junior_debt"
                name="account.knowledge_unsecured_junior_debt"
                class="mb-4"
                :required="isRequiredRequiredNaturalProfileAttributes('knowledge_unsecured_junior_debt')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('knowledge_unsecured_junior_debt')"
                :label="$t('account.knowledge_unsecured_junior_debt')" />
            <BaseInputSelect
                v-model="account.experience_unsecured_junior_debt"
                :label="$t('account.experience_unsecured_junior_debt')"
                name="account.experience_unsecured_junior_debt"
                :required="isRequiredRequiredNaturalProfileAttributes('experience_unsecured_junior_debt')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('experience_unsecured_junior_debt')"
                :icon="CalendarIcon"
                :options="investmentExperienceOptions">
            </BaseInputSelect>
        </div>
        <div class="sm:col-span-2">
            <div class="border-t border-gray-300 pb-4" />
            <p class="text-md font-semibold text-gray-700">{{ $t('account.experience_with_securities') }}</p>
        </div>

        <div class="sm:col-span-1">
            <BaseInputSelect
                v-model="account.experience_without_advice"
                :label="$t('account.experience_without_advice')"
                :icon="CalendarIcon"
                :required="isRequiredRequiredNaturalProfileAttributes('experience_without_advice')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('experience_without_advice')"
                name="account.experience_without_advice"
                :options="investmentExperienceOptions">
            </BaseInputSelect>
        </div>

        <div class="sm:col-span-1">
            <BaseInputSelect
                v-model="account.experience_with_investment_advice"
                :label="$t('account.experience_with_investment_advice')"
                :required="isRequiredRequiredNaturalProfileAttributes('experience_with_investment_advice')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('experience_with_investment_advice')"
                name="account.experience_with_investment_advice"
                :icon="CalendarIcon"
                :options="investmentExperienceOptions">
            </BaseInputSelect>
        </div>

        <div class="sm:col-span-1">
            <BaseInputSelect
                v-model="account.experience_with_asset_management"
                :label="$t('account.experience_with_asset_management')"
                name="account.experience_with_asset_management"
                :required="isRequiredRequiredNaturalProfileAttributes('experience_with_asset_management')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('experience_with_asset_management')"
                :icon="CalendarIcon"
                :options="investmentExperienceOptions">
            </BaseInputSelect>
        </div>

        <div class="sm:col-span-2">
            <div class="border-t border-gray-300 pb-4" />
            <p class="text-md font-semibold text-gray-700">{{ $t('account.investment_transactions') }}</p>
        </div>

        <div class="sm:col-span-1">
            <BaseInputSelect
                v-model="account.average_investment_transactions_per_year"
                :label="$t('account.average_investment_transactions_per_year')"
                :required="isRequiredRequiredNaturalProfileAttributes('average_investment_transactions_per_year')"
                :required-2="
                    isRequiredNaturalProfileAttributesForInvestment('average_investment_transactions_per_year')
                "
                name="account.average_investment_transactions_per_year"
                :icon="SwitchHorizontalIcon"
                :options="averageInvestmentTransactionOptions">
            </BaseInputSelect>
        </div>

        <div class="flex flex-col justify-end sm:col-span-1">
            <BaseInputSelect
                v-model="account.average_investment_transaction_amount"
                :label="$t('account.average_investment_transaction_amount')"
                :icon="CurrencyEuroIcon"
                :required="isRequiredRequiredNaturalProfileAttributes('average_investment_transaction_amount')"
                :required-2="isRequiredNaturalProfileAttributesForInvestment('average_investment_transaction_amount')"
                name="account.average_investment_transaction_amount"
                :options="averageInvestmentAmountOptions">
            </BaseInputSelect>
        </div>
    </div>
</template>
